import { getRuntimeAmplifyOutputs } from './oauthRuntimeConfig';

export type YstnjAuthAuthority = {
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId: string;
  oauthDomain: string;
  appsyncUrl: string;
  redirectSignInUrls: string[];
  redirectSignOutUrls: string[];
  origin: string | null;
  originSupported: boolean;
};

const normalizeText = (value: unknown): string => String(value ?? '').trim();

export const normalizeOrigin = (
  value: string | null | undefined,
): string | null => {
  const trimmed = normalizeText(value);
  if (!trimmed) {
    return null;
  }
  try {
    const parsed = new URL(trimmed);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return null;
  }
};

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map((entry) => normalizeText(entry))
        .filter((entry) => entry.length > 0)
    : [];

const currentOrigin = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const origin =
    typeof window.location?.origin === 'string' ? window.location.origin : null;
  return normalizeOrigin(origin);
};

const originMatches = (
  urls: readonly string[],
  origin: string | null,
): boolean => {
  if (!origin) {
    return false;
  }
  return urls.some((candidate) => normalizeOrigin(candidate) === origin);
};

export const resolveUserPoolIdFromIssuer = (issuer: unknown): string => {
  const normalized = normalizeText(issuer).replace(/\/+$/u, '');
  if (!normalized) {
    return '';
  }
  const lastSlash = normalized.lastIndexOf('/');
  return lastSlash === -1 ? '' : normalized.slice(lastSlash + 1);
};

export const resolveYstnjAuthAuthority = ({
  origin = currentOrigin(),
}: {
  origin?: string | null;
} = {}): YstnjAuthAuthority => {
  const runtimeOutputs = getRuntimeAmplifyOutputs({ origin }) as {
    auth?: {
      user_pool_id?: string;
      user_pool_client_id?: string;
      identity_pool_id?: string;
      oauth?: {
        domain?: string;
        redirect_sign_in_uri?: string[];
        redirect_sign_out_uri?: string[];
      };
    };
    data?: {
      url?: string;
    };
  };

  const redirectSignInUrls = toStringArray(
    runtimeOutputs.auth?.oauth?.redirect_sign_in_uri,
  );
  const redirectSignOutUrls = toStringArray(
    runtimeOutputs.auth?.oauth?.redirect_sign_out_uri,
  );
  const normalizedOrigin = normalizeOrigin(origin);

  return {
    userPoolId: normalizeText(runtimeOutputs.auth?.user_pool_id),
    userPoolClientId: normalizeText(runtimeOutputs.auth?.user_pool_client_id),
    identityPoolId: normalizeText(runtimeOutputs.auth?.identity_pool_id),
    oauthDomain: normalizeText(runtimeOutputs.auth?.oauth?.domain),
    appsyncUrl: normalizeText(runtimeOutputs.data?.url),
    redirectSignInUrls,
    redirectSignOutUrls,
    origin: normalizedOrigin,
    originSupported:
      originMatches(redirectSignInUrls, normalizedOrigin)
      && originMatches(redirectSignOutUrls, normalizedOrigin),
  };
};
